import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface Question {
  id: number;
  question: string;
  answer: string;
  created_at: string;
  updated_at: string;
}

interface SupportState {
  questions: Question[];
  searchQuery: string;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  page: number;
  hasMore: boolean;
  isMoreLoading: boolean;
}

const initialState: SupportState = {
  questions: [],
  searchQuery: "",
  isLoading: false,
  isSubmitting: false,
  error: null,
  page: 1,
  hasMore: true,
  isMoreLoading: false,
};

export const fetchSupportQuestions = createAsyncThunk(
  "support/fetchQuestions",
  async ({ query, page }: { query?: string; page?: number }, thunkAPI) => {
    try {
      const params: {
        search?: string;
        page?: number;
      } = {};
      if (query) {
        params.search = query;
      }
      if (page) {
        params.page = page;
      }
      const response = await axios.get(`${API_URL}/support`, {
        params,
      });

      const returnValue = {
        items: response.data.data.data,
        hasMore: !!response.data.data.next_page_url,
      };
      return returnValue;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to find questions",
      );
    }
  },
);

export const submitSupportEmail = createAsyncThunk(
  "support/submitEmail",
  async (
    formData: { email: string; subject: string; body: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(`${API_URL}/support/contact`, formData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Submission failed",
      );
    }
  },
);

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.page = 1;
      state.questions = [];
    },
    incrementPage: (state) => {
      state.page += 1;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportQuestions.pending, (state) => {
        if (state.page === 1) {
          state.isLoading = true;
        } else {
          state.isMoreLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchSupportQuestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isMoreLoading = false;
        if (state.page === 1) {
          state.questions = action.payload.items;
        } else {
          state.questions = [...state.questions, ...action.payload.items];
        }
        state.hasMore = action.payload.hasMore;
      })
      .addCase(fetchSupportQuestions.rejected, (state, action) => {
        state.isLoading = false;
        state.isMoreLoading = false;
        state.error = action.payload as string;
      })

      .addCase(submitSupportEmail.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitSupportEmail.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(submitSupportEmail.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });
  },
});

export const { setQuery, incrementPage, clearError } = supportSlice.actions;
export default supportSlice.reducer;
